import React, { useState } from "react";
import {
  Stethoscope,
  User,
  Bot,
  Loader,
  Plus,
  Trash2,
  Pencil,
  Ban,
  Scale,
  Ruler,
  Heart,
  Activity,
  Eye,
} from "lucide-react";
import { useEvolucao } from "../context/EvolucaoContext";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
  podeEscreverClinico,
} from "../../../shared/utils/permissoesCategoria";
import {
  Evolucao,
  Paciente,
  TIPO_CONSULTA_LABELS,
  TipoConsulta,
} from "../../../types";
import { theme } from "../../../shared/styles/theme";
import NovaEvolucao from "./NovaEvolucao";
import { useAnaliseContextual } from "../../ia-analise/hooks/useAnaliseContextual";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import EditarEvolucao from "./EditarEvolucao";

interface TimelineEvolucoesProps {
  pacienteId: string;
  pacienteNome: string;
  paciente: Paciente; // 👈 ADICIONAR
}

export default function TimelineEvolucoes({
  pacienteId,
  pacienteNome,
  paciente, // 👈 ADICIONAR
}: TimelineEvolucoesProps) {
  const { listarEvolucoesPorPaciente, removerEvolucao, cancelarEvolucao } =
    useEvolucao();
  const { loading, erro, analisarEvolucao } = useAnaliseContextual();
  const { usuarioLogado } = useAuth();
  // 🔐 B88: registrar/editar/excluir consulta = equipe clínica (médico +
  // multiprofissional); analisar com IA = só médico. Espelho das
  // firestore.rules — administrativo e convidado não veem os botões.
  const podeRegistrarConsulta =
    ehMedico(usuarioLogado) || ehMultiprofissional(usuarioLogado);
  const podeAnalisarIA = ehMedico(usuarioLogado);
  // 🔐 §6.3.2/B88 (AI_145): LEITURA da análise salva ("Ver análise anterior") é
  // liberada à equipe (não-médico lê o output já produzido); só a GERAÇÃO
  // ("Analisar") é ato médico. Espelha o padrão das análises setoriais.
  const podeLerAnaliseIA = podeEscreverClinico(usuarioLogado);
  // B323 Bloco 3 (AI_146): a janela de EDIÇÃO/EXCLUSÃO física conta a partir de
  // criadoEm (imutável, igual à regra do Firestore) — NÃO da data da consulta,
  // que é editável. Removido o escape de admin (D1): nem o admin sobrescreve
  // conteúdo finalizado; fora da janela, corrige-se por CANCELAMENTO com motivo.
  // Sem criadoEm (legado) = fora da janela (a regra ainda decide).
  const dentroJanelaEvolucao = (evolucao: Evolucao): boolean => {
    if (!evolucao.criadoEm) return false;
    const horas =
      (Date.now() - new Date(evolucao.criadoEm).getTime()) / (1000 * 60 * 60);
    return horas < 24;
  };
  const [mostrarNovaEvolucao, setMostrarNovaEvolucao] = useState(false);
  const [evolucoesExpandidas, setEvolucoesExpandidas] = useState<Set<string>>(
    new Set()
  );
  // 🤖 B314/AI_141 — análise de consulta em MODAL (mesmo padrão de marcadores/
  // sorologias): abre, não fecha ao clicar fora, e fica salva em "Ver análise
  // anterior" (persistida no paciente, com o id da consulta).
  const { atualizarPaciente } = usePacientes();
  const [mostrarAnaliseModal, setMostrarAnaliseModal] = useState(false);
  const [textoAnaliseExibido, setTextoAnaliseExibido] = useState<string | null>(
    null
  );
  const [dataAnaliseExibida, setDataAnaliseExibida] = useState<string | null>(
    null
  );
  const [ultimaAnaliseLocal, setUltimaAnaliseLocal] = useState<{
    texto: string;
    geradaEm: string;
    evolucaoId: string;
  } | null>(null);
  const ultimaAnaliseDisponivel =
    paciente.ultimaAnaliseEvolucao || ultimaAnaliseLocal;
  const [evolucaoAnalisando, setEvolucaoAnalisando] = useState<string | null>(
    null
  );
  const [evolucaoEditando, setEvolucaoEditando] = useState<any>(null);

  const evolucoes = listarEvolucoesPorPaciente(pacienteId);

  const toggleExpansao = (id: string) => {
    setEvolucoesExpandidas((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }
      return proximo;
    });
  };

  // "Expandir tudo" / "Recolher tudo": botão único que alterna conforme o estado.
  const todasExpandidas =
    evolucoes.length > 0 &&
    evolucoes.every((e) => evolucoesExpandidas.has(e.id));

  const toggleExpandirTudo = () => {
    setEvolucoesExpandidas(
      todasExpandidas ? new Set() : new Set(evolucoes.map((e) => e.id))
    );
  };

  const handleRemover = (id: string, data: string) => {
    if (
      window.confirm(
        `Tem certeza que deseja remover a consulta de ${new Date(
          data
        ).toLocaleString("pt-BR")}?`
      )
    ) {
      removerEvolucao(id);
    }
  };

  // B323 Bloco 3 (AI_146): cancelar consulta antiga (não-destrutivo, com motivo).
  const handleCancelar = async (id: string, data: string) => {
    const motivo = window.prompt(
      `Motivo do cancelamento da consulta de ${new Date(data).toLocaleDateString(
        "pt-BR"
      )} (obrigatório):`
    );
    if (motivo == null) return; // fechou o prompt
    if (!motivo.trim()) {
      alert("É obrigatório informar o motivo do cancelamento.");
      return;
    }
    try {
      await cancelarEvolucao(id, motivo);
    } catch (e) {
      alert("Não foi possível cancelar a consulta. Tente novamente.");
    }
  };

  // 🤖 FUNÇÃO PARA ANALISAR EVOLUÇÃO — abre o modal já em loading, gera, persiste
  const handleAnalisar = async (evolucaoId: string) => {
    setEvolucaoAnalisando(evolucaoId);
    setTextoAnaliseExibido(null);
    setDataAnaliseExibida(null);
    setMostrarAnaliseModal(true);
    try {
      const resultado = await analisarEvolucao(paciente, evolucaoId);
      if (resultado) {
        // criarAnaliseDeTexto coloca o texto integral em principaisAchados.
        const texto =
          (resultado.resumo?.principaisAchados || []).join("\n\n") ||
          "Sem resposta da IA";
        const agora = new Date().toISOString();
        setTextoAnaliseExibido(texto);
        setDataAnaliseExibida(agora);
        setUltimaAnaliseLocal({ texto, geradaEm: agora, evolucaoId });
        // 💾 Persiste a última análise no paciente (durável) — soft-fail.
        try {
          await atualizarPaciente(pacienteId, {
            ultimaAnaliseEvolucao: {
              texto,
              geradaEm: agora,
              evolucaoId,
              geradaPor: usuarioLogado?.id || "",
              geradaPorNome: usuarioLogado?.nome || "",
            },
          });
        } catch (e) {
          console.warn(
            "⚠️ Não foi possível salvar a última análise de consulta:",
            e
          );
        }
      }
      // Se `resultado` for null, o hook já populou `erro` — o modal o exibe.
    } catch (error) {
      console.error("Erro ao analisar evolução:", error);
    } finally {
      setEvolucaoAnalisando(null);
    }
  };

  const verUltimaAnalise = () => {
    const ua = ultimaAnaliseDisponivel;
    if (!ua?.texto) return;
    setTextoAnaliseExibido(ua.texto);
    setDataAnaliseExibida(ua.geradaEm || null);
    setMostrarAnaliseModal(true);
  };

  const fecharAnalise = () => {
    setMostrarAnaliseModal(false);
    setTextoAnaliseExibido(null);
    setDataAnaliseExibida(null);
  };

  const formatarData = (dataISO: string) => {
    const data = new Date(dataISO);
    return {
      data: data.toLocaleDateString("pt-BR"),
      hora: data.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const cardStyle: React.CSSProperties = {
    background: "white",
    borderRadius: theme.borderRadius.lg,
    padding: "20px",
    marginBottom: "16px",
    boxShadow: theme.shadows.md,
    border: `2px solid ${theme.gray[200]}`,
    transition: theme.transitions.normal,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: "700",
    color: theme.primary[700],
    marginTop: "16px",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: theme.fonts.sans,
  };

  const textStyle: React.CSSProperties = {
    fontSize: "14px",
    color: theme.gray[700],
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
    fontFamily: theme.fonts.sans,
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "16px",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            margin: 0,
            fontFamily: theme.fonts.sans,
          }}
        >
          <Stethoscope size={18} style={{ marginRight: "8px" }} /> Consultas
        </h3>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {evolucoes.length > 0 && (
            <button
              onClick={toggleExpandirTudo}
              style={{
                background: theme.gray[100],
                color: theme.gray[700],
                border: "none",
                padding: "12px 18px",
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                transition: theme.transitions.normal,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = theme.gray[200];
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = theme.gray[100];
              }}
            >
              {todasExpandidas ? "▲ Recolher tudo" : "▼ Expandir tudo"}
            </button>
          )}
          {podeRegistrarConsulta && (
            <button
              onClick={() => setMostrarNovaEvolucao(true)}
              style={{
                background: theme.primary[600],
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                transition: theme.transitions.normal,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = theme.primary[700];
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = theme.primary[600];
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Nova Consulta
            </button>
          )}
        </div>
      </div>

      {/* LISTA DE EVOLUÇÕES */}
      {evolucoes.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: theme.gray[500],
            fontFamily: theme.fonts.sans,
          }}
        >
          <Stethoscope
            size={48}
            color={theme.gray[300]}
            style={{ margin: "0 0 16px 0" }}
          />
          <p
            style={{
              fontSize: "16px",
              fontWeight: "600",
              margin: "0 0 8px 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            Nenhuma consulta registrada
          </p>
          <p
            style={{
              fontSize: "14px",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            Clique em "Nova Consulta" para começar
          </p>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          {/* LINHA DO TEMPO */}
          <div
            style={{
              position: "absolute",
              left: "20px",
              top: "0",
              bottom: "0",
              width: "2px",
              background: theme.primary[200],
            }}
          />

          {/* CARDS DAS EVOLUÇÕES */}
          {evolucoes.map((evolucao) => {
            const { data, hora } = formatarData(evolucao.data);
            const expandida = evolucoesExpandidas.has(evolucao.id);
            const analisando = evolucaoAnalisando === evolucao.id;

            return (
              <div
                key={evolucao.id}
                style={{
                  position: "relative",
                  paddingLeft: "60px",
                  marginBottom: "24px",
                }}
              >
                {/* ÍCONE NA TIMELINE */}
                <div
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "8px",
                    width: "26px",
                    height: "26px",
                    borderRadius: "50%",
                    background: theme.primary[600],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                    boxShadow: theme.shadows.md,
                  }}
                >
                  <Stethoscope size={16} />
                </div>

                {/* CARD */}
                <div style={cardStyle}>
                  {/* HEADER DO CARD */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: evolucao.cancelado
                            ? theme.gray[500]
                            : theme.gray[800],
                          textDecoration: evolucao.cancelado
                            ? "line-through"
                            : "none",
                          margin: "0 0 4px 0",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {data} às {hora}
                        {evolucao.cancelado && (
                          <span
                            title={evolucao.motivoCancelamento || ""}
                            style={{
                              marginLeft: "8px",
                              padding: "2px 8px",
                              background: "#fee2e2",
                              color: "#b91c1c",
                              borderRadius: theme.borderRadius.md,
                              fontSize: "11px",
                              fontWeight: "700",
                              textDecoration: "none",
                              verticalAlign: "middle",
                            }}
                          >
                            <Ban
                              size={11}
                              style={{ marginRight: "3px", verticalAlign: "-1px" }}
                            />
                            Cancelada
                          </span>
                        )}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          color: theme.gray[600],
                          margin: 0,
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <>
                          <User size={12} style={{ marginRight: "4px" }} />
                          {evolucao.registradoPorNome}
                        </>
                      </p>
                      {evolucao.cancelado && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#b91c1c",
                            margin: "4px 0 0 0",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          Cancelada
                          {evolucao.canceladoPorNome
                            ? ` por ${evolucao.canceladoPorNome}`
                            : ""}
                          {evolucao.motivoCancelamento
                            ? ` — ${evolucao.motivoCancelamento}`
                            : ""}
                        </p>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                      {/* 🤖 BOTÃO ANALISAR (não em consulta cancelada) */}
                      {podeAnalisarIA && !evolucao.cancelado && (
                        <button
                          onClick={() => handleAnalisar(evolucao.id)}
                          disabled={analisando}
                          style={{
                            background: analisando
                              ? theme.gray[300]
                              : "#8b5cf6",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: theme.borderRadius.md,
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: analisando ? "not-allowed" : "pointer",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {analisando ? (
                            <>
                              <Loader
                                size={14}
                                style={{ marginRight: "4px" }}
                              />
                              Analisando...
                            </>
                          ) : (
                            <>
                              <Bot size={14} style={{ marginRight: "4px" }} />
                              Analisar
                            </>
                          )}
                        </button>
                      )}
                      {/* 👁️ B314/AI_141 — reabre a última análise salva DESTA
                          consulta, sem gerar de novo. */}
                      {podeLerAnaliseIA &&
                        ultimaAnaliseDisponivel?.texto &&
                        ultimaAnaliseDisponivel.evolucaoId === evolucao.id && (
                          <button
                            onClick={verUltimaAnalise}
                            style={{
                              background: "white",
                              color: theme.primary[700],
                              border: `1px solid ${theme.primary[300]}`,
                              padding: "6px 12px",
                              borderRadius: theme.borderRadius.md,
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              fontFamily: theme.fonts.sans,
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Reabrir a última análise gerada desta consulta"
                          >
                            <Eye size={14} style={{ marginRight: "4px" }} />
                            Ver análise anterior
                          </button>
                        )}
                      <button
                        onClick={() => toggleExpansao(evolucao.id)}
                        style={{
                          background: theme.gray[100],
                          color: theme.gray[700],
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: theme.borderRadius.md,
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {expandida ? "▲ Recolher" : "▼ Expandir"}
                      </button>
                      {/* B323 Bloco 3 (AI_146): dentro de 24h → editar/remover
                          (engano recente); fora de 24h → CANCELAR com motivo
                          (não-destrutivo, preserva o original); cancelada → sem
                          ações. Vale igual para o admin (sem escape). */}
                      {podeRegistrarConsulta &&
                        !evolucao.cancelado &&
                        (dentroJanelaEvolucao(evolucao) ? (
                          <>
                            <button
                              onClick={() => setEvolucaoEditando(evolucao)}
                              style={{
                                background: "#dbeafe",
                                color: "#1d4ed8",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: theme.borderRadius.md,
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                                fontFamily: theme.fonts.sans,
                              }}
                            >
                              <Pencil
                                size={14}
                                style={{ marginRight: "6px" }}
                              />{" "}
                              Editar
                            </button>
                            <button
                              onClick={() =>
                                handleRemover(evolucao.id, evolucao.data)
                              }
                              style={{
                                background: "#fee2e2",
                                color: "#dc2626",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: theme.borderRadius.md,
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                                fontFamily: theme.fonts.sans,
                              }}
                            >
                              <Trash2
                                size={14}
                                style={{ marginRight: "6px" }}
                              />{" "}
                              Remover
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() =>
                              handleCancelar(evolucao.id, evolucao.data)
                            }
                            title="Cancelar esta consulta com motivo (ela fica registrada, marcada como cancelada — o original é preservado)"
                            style={{
                              background: "#fef3c7",
                              color: "#b45309",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: theme.borderRadius.md,
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            <Ban size={14} style={{ marginRight: "6px" }} />{" "}
                            Cancelar
                          </button>
                        ))}
                    </div>
                  </div>
                  {/* SINAIS VITAIS */}
                  {(evolucao.peso ||
                    evolucao.altura ||
                    evolucao.fc ||
                    evolucao.pa) && (
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        marginBottom: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {evolucao.peso && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#374151",
                            background: "#f1f5f9",
                            padding: "3px 10px",
                            borderRadius: "999px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Scale size={12} style={{ marginRight: "4px" }} />{" "}
                          {evolucao.peso} kg
                        </span>
                      )}
                      {evolucao.altura && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#374151",
                            background: "#f1f5f9",
                            padding: "3px 10px",
                            borderRadius: "999px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Ruler size={12} style={{ marginRight: "4px" }} />{" "}
                          {evolucao.altura} cm
                        </span>
                      )}
                      {evolucao.fc && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#374151",
                            background: "#f1f5f9",
                            padding: "3px 10px",
                            borderRadius: "999px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Heart size={12} style={{ marginRight: "4px" }} />{" "}
                          {evolucao.fc} bpm
                        </span>
                      )}
                      {evolucao.pa && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#374151",
                            background: "#f1f5f9",
                            padding: "3px 10px",
                            borderRadius: "999px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Activity size={12} style={{ marginRight: "4px" }} />{" "}
                          {evolucao.pa} mmHg
                        </span>
                      )}
                    </div>
                  )}

                  {/* B90: TIPO DE CONSULTA (rótulo — sempre visível; o texto da
                      evolução livre fica no bloco expandido, mais abaixo) */}
                  {evolucao.tipoConsulta && (
                    <span
                      style={{
                        display: "inline-block",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6d28d9",
                        background: "#f5f3ff",
                        border: "1px solid #ddd6fe",
                        borderRadius: "999px",
                        padding: "2px 10px",
                        marginBottom: "8px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {TIPO_CONSULTA_LABELS[
                        evolucao.tipoConsulta as TipoConsulta
                      ] || evolucao.tipoConsulta}
                    </span>
                  )}

                  {/* QUEIXA PRINCIPAL (SEMPRE VISÍVEL) */}
                  {evolucao.queixaPrincipal && (
                    <div>
                      <p style={sectionTitleStyle}>Queixa Principal</p>
                      <p style={textStyle}>{evolucao.queixaPrincipal}</p>
                    </div>
                  )}

                  {/* PRÉVIA (colapsado): primeiras linhas da evolução livre.
                      Retorno/pós-Tx/intercorrência usam o campo único e antes
                      mostravam só o rótulo — agora dá p/ entender o assunto sem
                      precisar expandir. Limitado a ~3 linhas (line-clamp). */}
                  {!expandida && evolucao.evolucaoLivre && (
                    <p
                      style={{
                        ...textStyle,
                        margin: "4px 0 0 0",
                        color: theme.gray[600],
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {evolucao.evolucaoLivre}
                    </p>
                  )}

                  {/* CONTEÚDO EXPANDIDO */}
                  {expandida && (
                    <>

                      {/* B90: EVOLUÇÃO (campo único) — só no expandido */}
                      {evolucao.evolucaoLivre && (
                        <div>
                          <p style={sectionTitleStyle}>Evolução</p>
                          <p style={{ ...textStyle, whiteSpace: "pre-wrap" }}>
                            {evolucao.evolucaoLivre}
                          </p>
                        </div>
                      )}

                      {evolucao.historiaDoencaAtual && (
                        <div>
                          <p style={sectionTitleStyle}>
                            História da Doença Atual
                          </p>
                          <p style={textStyle}>
                            {evolucao.historiaDoencaAtual}
                          </p>
                        </div>
                      )}

                      {evolucao.revisaoSistemas && (
                        <div>
                          <p style={sectionTitleStyle}>Revisão de Sistemas</p>
                          <p style={textStyle}>{evolucao.revisaoSistemas}</p>
                        </div>
                      )}

                      {evolucao.exameFisico && (
                        <div>
                          <p style={sectionTitleStyle}>Exame Físico</p>
                          <p style={textStyle}>{evolucao.exameFisico}</p>
                        </div>
                      )}

                      {evolucao.hipotesesDiagnosticas && (
                        <div>
                          <p style={sectionTitleStyle}>
                            Hipóteses Diagnósticas
                          </p>
                          <p style={textStyle}>
                            {evolucao.hipotesesDiagnosticas}
                          </p>
                        </div>
                      )}

                      {evolucao.conduta && (
                        <div>
                          <p style={sectionTitleStyle}>
                            Conduta / Plano Terapêutico
                          </p>
                          <p style={textStyle}>{evolucao.conduta}</p>
                        </div>
                      )}

                      {evolucao.observacoes && (
                        <div>
                          <p style={sectionTitleStyle}>Observações</p>
                          <p style={textStyle}>{evolucao.observacoes}</p>
                        </div>
                      )}

                      {/* RODAPÉ COM INFO DE EDIÇÃO */}
                      {evolucao.editadoPorNome && (
                        <div
                          style={{
                            marginTop: "16px",
                            paddingTop: "16px",
                            borderTop: `1px solid ${theme.gray[200]}`,
                            fontSize: "12px",
                            color: theme.gray[500],
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Pencil size={12} style={{ marginRight: "4px" }} />{" "}
                          Editado por {evolucao.editadoPorNome} em{" "}
                          {evolucao.dataEdicao &&
                            (() => {
                              let d = evolucao.dataEdicao;
                              if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
                              const dt = new Date(d);
                              return isNaN(dt.getTime())
                                ? ""
                                : dt.toLocaleString("pt-BR");
                            })()}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL DE NOVA EVOLUÇÃO */}
      {mostrarNovaEvolucao && (
        <NovaEvolucao
          pacienteId={pacienteId}
          pacienteNome={pacienteNome}
          onClose={() => setMostrarNovaEvolucao(false)}
          onSucesso={() => {
            // Atualizar lista automaticamente
          }}
        />
      )}
      {evolucaoEditando && (
        <EditarEvolucao
          evolucao={evolucaoEditando}
          pacienteNome={pacienteNome}
          onClose={() => setEvolucaoEditando(null)}
          onSucesso={() => setEvolucaoEditando(null)}
        />
      )}

      {/* 🤖 MODAL DA ANÁLISE DA CONSULTA (B314/AI_141) — padrão marcadores/
          sorologias: abre, NÃO fecha ao clicar fora (só X/Fechar). */}
      {mostrarAnaliseModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  🤖 Análise da Consulta
                </h2>
                {dataAnaliseExibida && (
                  <span
                    style={{
                      fontSize: "12px",
                      opacity: 0.85,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Gerada em{" "}
                    {new Date(dataAnaliseExibida).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
              <button
                onClick={fecharAnalise}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  color: "white",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {loading && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px",
                    padding: "40px",
                    color: theme.gray[600],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Loader size={32} className="animate-spin" />
                  <p>Avaliando as condutas da consulta…</p>
                </div>
              )}
              {erro && !loading && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: theme.borderRadius.md,
                    padding: "16px",
                    color: "#dc2626",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {erro}
                </div>
              )}
              {textoAnaliseExibido && !loading && (
                <TextoAnaliseMarkdown texto={textoAnaliseExibido} />
              )}
            </div>

            {!loading && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: `1px solid ${theme.gray[200]}`,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={fecharAnalise}
                  style={{
                    padding: "10px 24px",
                    background: theme.gray[100],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
